// AiAssistantProvider — owns the conversation, the tenant it belongs to, and
// the one in-flight request.
//
// ─── TENANT SAFETY IS THE POINT OF THIS FILE ───────────────────────────────
// `useTenantId()` is deliberately NOT used here. It falls back to
// `env.tenantId`, whose default is a REAL tenant UUID, and a wrong `tenant_id`
// makes this server answer **200 with a plausible empty result** — "There are no
// accounts." — that nothing in the response distinguishes from a true empty
// result. There is no error to catch and no test that would notice. So the
// tenant is read straight off the verified session claim, `null` is treated as
// "cannot ask", and the tenant a conversation was asked as is stamped on it and
// re-checked on every render.
//
// ─── THE CANCEL/OFFLINE TRAP ───────────────────────────────────────────────
// The shared api-client surfaces an externally-aborted request as
// `ApiError { kind: 'network' }` — byte-identical to a dead network. So
// `cancelledByUserRef` is set BEFORE `abort()` is called and handed to
// `toAiFailure`. Without that ordering, pressing Stop renders "Check your
// connection", which is a lie no type error can catch.
//
// ─── AND THE SIGNAL ALONE IS NOT ENOUGH TO STOP AN ASK ─────────────────────
// Verified in `createApiClient.ts`: cancellation is wired as
// `externalSignal.addEventListener('abort', …)`, so an abort that was ALREADY
// dispatched before the call never fires that listener — the internal controller
// stays unaborted and the request SUCCEEDS, with no error and no rejection. Two
// consequences are built in below:
//
//   1. A fresh `AbortController` per ask, created immediately before `mutate`.
//      Never reused, never carried over from a cancelled ask — a reused,
//      already-aborted controller would make the NEXT question uncancellable.
//   2. `onSuccess` honours `cancelledByUserRef` anyway. If the operator pressed
//      Stop, the turn is `cancelled` even when a reply lands, because "Stop did
//      nothing" is the exact symptom that factory behaviour produces. The
//      signal is the optimisation; the flag is the guarantee.
//
// ─── NO FALLBACK FOR AN UNCONFIGURED HOST ──────────────────────────────────
// `env.aiChatApiUrl` defaults to the empty string and the client then throws
// `TypeError: Invalid base URL` at CALL time. That is deliberate and loud, and
// `components/crm/CrmAssistantMount.tsx` already refuses to render the widget
// unless it is set. Do not add a soft fallback or a placeholder URL here — a
// second, quieter mitigation would hide the misconfiguration the first one
// exists to surface.
//
// ─── THIS MODULE IS A STATIC IMPORT ────────────────────────────────────────
// `CrmShell` imports the provider eagerly (a lazy provider would put the whole
// CRM behind a Suspense boundary); only the dock and the launcher are lazy. So
// keep this file's imports light — anything heavy belongs in the code-split
// components. The one deliberate exception is `useAiI18n`, whose dictionary must
// be registered before the live region can announce anything, and which the
// lazily-loaded panel therefore cannot own.
//
// ─── WHY THE REQUEST OUTLIVES THE PANEL ────────────────────────────────────
// Closing the panel does not abort. A data question costs ~7s of server work;
// throwing it away means paying it twice. The conversation survives route
// changes for the same reason — this provider sits above `<Outlet/>`.
//
// No transcript persistence in this slice. The only thing written to storage is
// the width preference, through `@fluxora/storage` (never `sessionStorage`
// directly — that is PR-blocking).

import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { useAuth } from '@fluxora/auth';
import { useTranslation } from '@fluxora/i18n';
import { createStorage } from '@fluxora/storage';

import { env } from '../../../config/env';
import { useAskRheoraAi } from '../api/aiChat.queries';
import { AiLiveRegion } from '../components/AiLiveRegion';
import { useGlobalHotkey } from '../hooks/useGlobalHotkey';
import { useAiI18n } from '../i18n/useAiI18n';
import type { ChatReply, ChatRequest } from '../schemas/chat.schema';
import { parseAiIntent } from '../services/aiIntent.service';
import {
  buildHistory,
  conversationReducer,
  conversationTenantMatches,
  createConversation,
} from '../services/conversation.service';
import { toAiFailure } from '../services/failure.service';
import {
  AiAssistantContext,
  type AiAssistantContextValue,
  type AiLiveAnnouncement,
} from './AiAssistantContext';

/** Session-scoped store. Width is a preference, not data — nothing else goes here. */
const prefs = createStorage('ai-assistant');
const WIDE_PREF_KEY = 'panel-wide';

/** The key the panel answers to, alongside Cmd/Ctrl. */
const TOGGLE_HOTKEY = 'k';

const SILENT: AiLiveAnnouncement = { text: '', level: 'polite' };

export interface AiAssistantProviderProps {
  readonly children: ReactNode;
}

export function AiAssistantProvider({ children }: AiAssistantProviderProps): JSX.Element {
  useAiI18n();
  const { t } = useTranslation();

  // Straight off the session claim. See the file header for why not useTenantId().
  const sessionTenantId = useAuth().user?.tenantId ?? null;

  const [isOpen, setIsOpen] = useState(false);
  const [isWide, setIsWideState] = useState<boolean>(() => {
    const stored = prefs.get<boolean>(WIDE_PREF_KEY);
    return typeof stored === 'boolean' ? stored : false;
  });
  const [hasSent, setHasSent] = useState(false);
  const [tenantDiscarded, setTenantDiscarded] = useState(false);
  const [prefill, setPrefill] = useState<AiAssistantContextValue['prefill']>(null);
  const [live, setLive] = useState<AiLiveAnnouncement>(SILENT);
  const [answerReadyWhileClosed, setAnswerReadyWhileClosed] = useState(false);
  const [focusLauncherNonce, setFocusLauncherNonce] = useState(0);

  const [conversation, dispatch] = useReducer(conversationReducer, undefined, () =>
    createConversation(sessionTenantId ?? '', crypto.randomUUID()),
  );

  const { mutate } = useAskRheoraAi();

  const abortRef = useRef<AbortController | null>(null);
  const cancelledByUserRef = useRef(false);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const isOpenRef = useRef(isOpen);
  /**
   * Which conversation generation a reply belongs to.
   *
   * Bumped on every reset. A reply that arrives after the tenant changed is
   * about data the operator can no longer see, so it is dropped silently rather
   * than announced as a failure — announcing "check your connection" for a
   * tenant switch is the same class of lie as the cancel/offline trap.
   */
  const generationRef = useRef(0);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const announce = useCallback((text: string, level: 'polite' | 'assertive'): void => {
    setLive({ text, level });
  }, []);

  // ── tenant stamp enforcement ────────────────────────────────────────────
  // Compared on every render, not only at sign-in: a token refresh that lands
  // on a different tenant is indistinguishable from a fresh session from here.
  useEffect(() => {
    if (sessionTenantId === null) return;
    if (conversationTenantMatches(conversation, sessionTenantId)) return;
    const hadTurns = conversation.turns.length > 0;
    generationRef.current += 1;
    cancelledByUserRef.current = false;
    abortRef.current?.abort();
    abortRef.current = null;
    dispatch({
      type: 'reset',
      tenantId: sessionTenantId,
      conversationId: crypto.randomUUID(),
    });
    setTenantDiscarded(hadTurns);
  }, [conversation, sessionTenantId]);

  // ── focus restoration on close ──────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;
      return;
    }
    if (!wasOpenRef.current) return;
    wasOpenRef.current = false;
    const previous = previousFocusRef.current;
    previousFocusRef.current = null;
    if (previous !== null && previous.isConnected) {
      previous.focus();
      return;
    }
    // The element that opened the panel is usually the launcher itself, which
    // unmounts while the panel is open — so "restore" nearly always means "give
    // focus back to the launcher", which remounts on the next paint.
    setFocusLauncherNonce((n) => n + 1);
  }, [isOpen]);

  const open = useCallback((): void => {
    const active = document.activeElement;
    previousFocusRef.current = active instanceof HTMLElement ? active : null;
    setAnswerReadyWhileClosed(false);
    setIsOpen(true);
  }, []);

  const close = useCallback((): void => {
    setIsOpen(false);
  }, []);

  const toggle = useCallback((): void => {
    if (isOpenRef.current) {
      close();
      return;
    }
    open();
  }, [close, open]);

  const setWide = useCallback((wide: boolean): void => {
    setIsWideState(wide);
    prefs.set(WIDE_PREF_KEY, wide);
  }, []);

  const ask = useCallback(
    (text: string): void => {
      const askId = crypto.randomUUID();
      const now = Date.now();

      // A tenant that vanished between render and submit. The composer is
      // already disabled without one, so this is the race, not the norm.
      if (sessionTenantId === null) {
        dispatch({ type: 'ask', askId, text, now });
        dispatch({ type: 'failed', askId, failure: { kind: 'no-tenant' }, retryText: text });
        announce(t('ai.live.failed'), 'assertive');
        return;
      }

      const generation = generationRef.current;
      // Fresh per ask, never reused. See the file header.
      const controller = new AbortController();
      abortRef.current = controller;
      cancelledByUserRef.current = false;

      const body: ChatRequest = {
        question: text,
        tenant_id: sessionTenantId,
        history: buildHistory(conversation.turns),
      };

      dispatch({ type: 'ask', askId, text, now });
      setHasSent(true);
      setTenantDiscarded(false);
      announce(t('ai.live.sending'), 'polite');

      mutate(
        { body, signal: controller.signal },
        {
          onSuccess: (reply: ChatReply): void => {
            if (generationRef.current !== generation) return;
            // The operator pressed Stop and the request went through anyway —
            // either the reply was already in flight, or the factory ignored an
            // already-dispatched abort. Their decision wins over the late reply:
            // rendering an answer somebody cancelled is how Stop stops being
            // believed.
            if (cancelledByUserRef.current) {
              dispatch({ type: 'failed', askId, failure: { kind: 'cancelled' }, retryText: text });
              announce(t('ai.live.stopped'), 'polite');
              return;
            }
            dispatch({ type: 'answered', askId, reply, now: Date.now() });
            if (!isOpenRef.current) setAnswerReadyWhileClosed(true);
            announce(
              parseAiIntent(reply.intent) === 'data_question'
                ? t('ai.live.answerReady')
                : t('ai.live.replied'),
              'polite',
            );
          },
          onError: (error: unknown): void => {
            if (generationRef.current !== generation) return;
            const failure = toAiFailure(error, {
              cancelledByUser: cancelledByUserRef.current,
            });
            dispatch({ type: 'failed', askId, failure, retryText: text });
            if (failure.kind === 'cancelled') {
              announce(t('ai.live.stopped'), 'polite');
              return;
            }
            announce(t('ai.live.failed'), 'assertive');
          },
        },
      );
    },
    [announce, conversation.turns, mutate, sessionTenantId, t],
  );

  const cancel = useCallback((): void => {
    // Order matters — see the file header.
    cancelledByUserRef.current = true;
    abortRef.current?.abort();
  }, []);

  const prefillComposer = useCallback((text: string): void => {
    setPrefill((current) => ({ text, nonce: (current?.nonce ?? 0) + 1 }));
  }, []);

  /**
   * Back to the suggested questions — i.e. an empty log.
   *
   * Mirrors the tenant-stamp reset above, and must: an answer still in flight
   * would otherwise land in the FRESH conversation seconds after the operator
   * cleared it, which reads as the panel answering a question nobody asked.
   * Bumping the generation makes the reducer drop that late reply, and the
   * abort stops the request paying for itself.
   *
   * `cancelledByUserRef` is deliberately NOT set: this is not a cancel, and
   * flagging it as one would surface the "you cancelled" affordance on a panel
   * the operator has just emptied.
   */
  const backToSuggestions = useCallback((): void => {
    if (sessionTenantId === null) return;
    generationRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    dispatch({
      type: 'reset',
      tenantId: sessionTenantId,
      conversationId: crypto.randomUUID(),
    });
    // Clear the "your conversation was discarded" banner if one was showing —
    // the operator did this on purpose and does not need telling.
    setTenantDiscarded(false);
    setPrefill(null);
  }, [sessionTenantId]);

  useGlobalHotkey({
    enabled: env.aiAssistantHotkeyEnabled,
    key: TOGGLE_HOTKEY,
    onTrigger: toggle,
  });

  // Derived, never stored: a `useState<LoadState>` beside the reducer is exactly
  // how Send and the pending bubble end up disagreeing about what is in flight.
  const isPending = conversation.turns.some((turn) => turn.kind === 'pending');

  const value = useMemo<AiAssistantContextValue>(
    () => ({
      isOpen,
      isWide,
      conversation,
      isPending,
      hasSent,
      sessionTenantId,
      canAsk: sessionTenantId !== null,
      tenantDiscarded,
      prefill,
      live,
      answerReadyWhileClosed,
      focusLauncherNonce,
      open,
      close,
      toggle,
      setWide,
      ask,
      cancel,
      retry: ask,
      prefillComposer,
      backToSuggestions,
    }),
    [
      answerReadyWhileClosed,
      ask,
      cancel,
      close,
      conversation,
      focusLauncherNonce,
      hasSent,
      isOpen,
      isPending,
      isWide,
      live,
      open,
      prefill,
      prefillComposer,
      backToSuggestions,
      sessionTenantId,
      setWide,
      tenantDiscarded,
      toggle,
    ],
  );

  return (
    <AiAssistantContext.Provider value={value}>
      {children}
      {/* Mounted here rather than inside the panel, so a reply that lands while
          the panel is closed is still announced. */}
      <AiLiveRegion message={live.text} level={live.level} />
    </AiAssistantContext.Provider>
  );
}
